<script setup lang="ts">
import { z } from 'zod'
import { logApiError } from '~/utils/logger'

interface TicketTypeData {
  id: string
  name: string
  description: string | null
  price: number
  type: 'week' | 'day'
  available_quantity: number | null
  max_quantity: number | null
  sale_start_date: string | null
  sale_end_date: string | null
  available_dates: unknown
  is_active: boolean
}

interface Props {
  modelValue: boolean
  campId: string
  campName: string
  ticketTypes?: TicketTypeData[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'joined': []
}>()

// Auth & Profile
const { isAuthenticated, user: supabaseUser } = useUser()
const isLoadingProfile = ref(false)

const { joinWaitlist } = useWaitlist()

const isOpen = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
})

const formData = ref({
  name: '',
  email: '',
  phone: '',
})

// Ticket selections - map of ticket type ID to quantity
const ticketSelections = ref<Record<string, number>>({})

// Initialize ticket selections when ticket types are provided
watch(() => props.ticketTypes, types => {
  if (types) {
    const newSelections: Record<string, number> = {}

    types.forEach(type => {
      newSelections[type.id] = 0
    })
    ticketSelections.value = newSelections
  }
}, { immediate: true })

const fetchUserProfile = async () => {
  if (!isAuthenticated.value)
    return

  isLoadingProfile.value = true
  try {
    const response = await $fetch<{ success: boolean, data: { full_name?: string, email?: string, phone?: string } }>('/api/user/profile')
    if (response.success && response.data) {
      formData.value.name = response.data.full_name || ''
      formData.value.email = response.data.email || ''
      formData.value.phone = response.data.phone || ''
    }
  }
  catch (error) {
    logApiError('/api/user/profile', error)
  }
  finally {
    isLoadingProfile.value = false
  }
}

// Fetch user profile when dialog opens for authenticated users
watch(isOpen, newValue => {
  if (newValue && isAuthenticated.value) {
    fetchUserProfile()
  }
})

const isSubmitting = ref(false)
const showSuccess = ref(false)
const position = ref<number | null>(null)

// Zod schema for form validation
const formSchema = z.object({
  name: z.string().min(2, 'Naam moet minimaal 2 karakters zijn'),
  email: z.string().email('Ongeldig e-mailadres').or(z.literal('')),
  phone: z.string().optional(),
})

const errors = ref<Record<string, string>>({})

// Computed total quantity from ticket selections
const totalQuantity = computed(() => {
  return Object.values(ticketSelections.value).reduce((sum, qty) => sum + (qty || 0), 0)
})

const validateForm = () => {
  errors.value = {}
  try {
    // Only validate personal fields if not authenticated
    if (!isAuthenticated.value) {
      formSchema.parse(formData.value)
    }

    // Validate at least one ticket type selected
    if (totalQuantity.value === 0) {
      errors.value.ticketSelections = 'Selecteer minimaal 1 ticket type'

      return false
    }

    if (totalQuantity.value > 10) {
      errors.value.ticketSelections = 'Maximaal 10 plaatsen in totaal'

      return false
    }

    return true
  }
  catch (error) {
    if (error instanceof z.ZodError) {
      error.issues.forEach(err => {
        if (err.path[0])
          errors.value[err.path[0] as string] = err.message
      })
    }

    return false
  }
}

const resetForm = () => {
  formData.value = {
    name: '',
    email: '',
    phone: '',
  }

  // Reset ticket selections to 0
  if (props.ticketTypes) {
    const newSelections: Record<string, number> = {}

    props.ticketTypes.forEach(type => {
      newSelections[type.id] = 0
    })
    ticketSelections.value = newSelections
  }
  errors.value = {}
  showSuccess.value = false
  position.value = null
}

const getWaitlistErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const errorPayload = error as { data?: { message?: string }, statusMessage?: string }

    return errorPayload.data?.message || errorPayload.statusMessage || 'Er is iets misgegaan'
  }

  return 'Er is iets misgegaan'
}

const handleSubmit = async () => {
  if (!validateForm())
    return

  isSubmitting.value = true

  try {
    // For authenticated users, use email from Supabase auth if form email is empty
    const emailToSend = isAuthenticated.value && !formData.value.email
      ? (supabaseUser.value?.email || '')
      : formData.value.email

    // Send the summed total quantity to the backend
    const response = await joinWaitlist({
      camp_id: props.campId,
      name: formData.value.name,
      email: emailToSend,
      phone: formData.value.phone,
      requested_quantity: totalQuantity.value,
    })

    // Store position if returned
    if (response?.position)
      position.value = response.position

    showSuccess.value = true
    emit('joined')

    // Reset form after 2 seconds and close dialog
    setTimeout(() => {
      resetForm()
      isOpen.value = false
    }, 3000)
  }
  catch (error) {
    logApiError('/api/waitlist/join', error, { campId: props.campId })

    // Show error message from API if available
    const errorMessage = getWaitlistErrorMessage(error)

    errors.value.general = errorMessage
  }
  finally {
    isSubmitting.value = false
  }
}

// Reset when dialog closes
watch(isOpen, newValue => {
  if (!newValue)
    setTimeout(resetForm, 300)
})
</script>

<template>
  <VDialog v-model="isOpen" max-width="600" persistent>
    <VCard>
      <VCardTitle class="d-flex align-center gap-2">
        <VIcon icon="bx-list-ul" />
        Inschrijven op Wachtlijst
      </VCardTitle>

      <VCardText>
        <VAlert v-if="!showSuccess" color="info" variant="tonal" class="mb-4">
          <p class="mb-2">
            <strong>{{ campName }}</strong> is momenteel uitverkocht.
          </p>
          <p class="mb-0">
            Schrijf je in op de wachtlijst en we sturen je een e-mail wanneer er weer tickets beschikbaar komen.
          </p>
        </VAlert>

        <VAlert v-if="showSuccess" color="success" variant="tonal" icon="bx-check-circle" class="mb-4">
          <p class="font-weight-bold mb-2">
            Je bent toegevoegd aan de wachtlijst!
          </p>
          <p v-if="position" class="mb-2">
            Je staat op positie <strong>{{ position }}</strong> in de rij.
          </p>
          <p class="mb-0">
            We sturen je een e-mail op <strong>{{ formData.email }}</strong> wanneer er tickets beschikbaar komen.
          </p>
        </VAlert>

        <VAlert
          v-if="errors.general" color="error" variant="tonal" icon="bx-error-circle" class="mb-4" closable
          @click:close="errors.general = ''"
        >
          {{ errors.general }}
        </VAlert>

        <VForm v-if="!showSuccess" @submit.prevent="handleSubmit">
          <VRow>
            <!-- Info alert for authenticated users -->
            <VCol v-if="isAuthenticated" cols="12">
              <VAlert type="info" variant="tonal" class="mb-2">
                <div class="text-body-2">
                  Wij gebruiken je profiel informatie voor de wachtlijst.
                </div>
              </VAlert>
            </VCol>

            <!-- Personal fields - only show if not authenticated -->
            <template v-if="!isAuthenticated">
              <VCol cols="12">
                <VTextField
                  v-model="formData.name" label="Volledige naam"
                  prepend-inner-icon="bx-user" :error-messages="errors.name" :disabled="isSubmitting" required
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="formData.email" label="E-mailadres" type="email"
                  prepend-inner-icon="bx-envelope" :error-messages="errors.email"
                  :disabled="isSubmitting" required
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="formData.phone" label="Telefoonnummer (optioneel)" type="tel"
                  prepend-inner-icon="bx-phone" :error-messages="errors.phone"
                  :disabled="isSubmitting"
                />
              </VCol>
            </template>

            <!-- Ticket type selector -->
            <VCol cols="12">
              <div class="text-body-2 font-weight-medium mb-3">
                Selecteer ticket types:
              </div>

              <div v-if="ticketTypes && ticketTypes.length > 0">
                <div v-for="(ticketType, index) in ticketTypes" :key="ticketType.id" class="mb-4">
                  <VRow align="center" no-gutters>
                    <VCol cols="7">
                      <div class="text-body-1 font-weight-medium">
                        {{ ticketType.name }}
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        €{{ ticketType.price.toFixed(2) }}{{ ticketType.type === 'day' ? ' / dag' : '' }}
                      </div>
                    </VCol>
                    <VCol cols="5">
                      <VTextField
                        v-model.number="ticketSelections[ticketType.id]" type="number" min="0" max="10"
                        density="compact" hide-details prepend-inner-icon="bx-ticket" :disabled="isSubmitting"
                      />
                    </VCol>
                  </VRow>
                  <VDivider v-if="index < ticketTypes.length - 1" class="mt-3" />
                </div>

                <div v-if="errors.ticketSelections" class="text-error text-caption mt-2">
                  {{ errors.ticketSelections }}
                </div>

                <div class="text-caption text-medium-emphasis mt-2">
                  Voor hoeveel personen per ticket type wil je geïnformeerd worden?
                </div>

                <!-- Show total quantity -->
                <div v-if="totalQuantity > 0" class="text-caption font-weight-medium mt-2">
                  Totaal: {{ totalQuantity }} {{ totalQuantity === 1 ? 'plaats' : 'plaatsen' }}
                </div>
              </div>

              <VAlert v-else type="warning" variant="tonal">
                Geen ticket types beschikbaar voor dit kamp.
              </VAlert>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn v-if="!showSuccess" variant="text" :disabled="isSubmitting" @click="isOpen = false">
          Annuleren
        </VBtn>
        <VBtn v-if="!showSuccess" color="primary" :loading="isSubmitting" @click="handleSubmit">
          <VIcon icon="bx-check" start />
          Inschrijven
        </VBtn>
        <VBtn v-else color="success" @click="isOpen = false">
          Sluiten
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
